import { useEffect, useRef } from 'react';

/**
 * Keymap registry (DESIGN.md §9.1, §11.10). Every shortcut of every app is listed here, once,
 * by action id. The default scheme is Outlook-style Ctrl chords (user decision 2026-10-09);
 * another scheme (e.g. Gmail single keys) can be added as a second map later.
 *
 * Chord syntax: modifiers `Mod` (Ctrl, or ⌘ on macOS), `Shift`, `Alt` joined with `+` and a
 * final key as in `KeyboardEvent.key` (case-insensitive), e.g. `Mod+Shift+R`, `Delete`, `F3`.
 */
export const KEYMAP_OUTLOOK = {
  // Global (§9.1)
  'app.commandPalette': ['Mod+K'],
  // Apps whose search bar takes Mod+K (webmail) open the palette here instead.
  'app.commandPaletteAlt': ['Mod+Shift+K'],
  'app.focusSearch': ['/', 'Mod+E', 'F3'],
  'app.help': ['F1'],
  // Opens the context menu of the focused item (DESIGN.md §18); right-click and long-press do the same.
  'app.contextMenu': ['Shift+F10', 'ContextMenu'],
  // Mail (§11.10)
  'mail.new': ['Mod+N', 'Alt+N'],
  'mail.reply': ['Mod+R'],
  'mail.replyAll': ['Mod+Shift+R'],
  'mail.forward': ['Mod+F'],
  'mail.forwardAsAttachment': ['Mod+Alt+F'],
  'mail.details': ['Mod+Alt+I'],
  'mail.viewSource': ['Mod+Alt+U'],
  'mail.print': ['Mod+P'],
  'mail.send': ['Mod+Enter'],
  // Composer (T11): attach a file (user request 2026-10-09); active only while a composer is open.
  'mail.attachFile': ['Mod+Shift+A'],
  // Recipient fields (T11): check names like Outlook; local to the field, the composer isolates it from the palette.
  'mail.checkNames': ['Mod+K'],
  'mail.markRead': ['Mod+Q'],
  'mail.markUnread': ['Mod+U'],
  'mail.delete': ['Delete'],
  'mail.move': ['Mod+Shift+V'],
  'mail.flag': ['Insert'],
  'mail.next': ['ArrowDown'],
  'mail.previous': ['ArrowUp'],
  'mail.open': ['Enter'],
  'mail.undo': ['Mod+Z'],
  // Calendar (active on the calendar page only; single keys like Google Calendar, Outlook's Alt+arrows).
  'calendar.new': ['N', 'C'],
  'calendar.quickAdd': ['Q'],
  'calendar.today': ['T'],
  'calendar.next': ['J', 'Alt+ArrowRight'],
  'calendar.previous': ['K', 'Alt+ArrowLeft'],
  'calendar.viewDay': ['D'],
  'calendar.viewWorkWeek': ['X'],
  'calendar.viewWeek': ['W'],
  'calendar.viewMonth': ['M'],
  'calendar.viewAgenda': ['A'],
  'calendar.goTo': ['G'],
  'calendar.search': ['S'],
  'calendar.open': ['Enter'],
  'calendar.delete': ['Delete'],
  'calendar.copy': ['Mod+C'],
  'calendar.paste': ['Mod+V'],
  'calendar.duplicate': ['Mod+D'],
  'calendar.help': ['Shift+?'],
  'calendar.print': ['Mod+P'],
} as const satisfies Record<string, readonly string[]>;

export type ShortcutAction = keyof typeof KEYMAP_OUTLOOK;
export type Keymap = Readonly<Record<ShortcutAction, readonly string[]>>;

let activeKeymap: Keymap = KEYMAP_OUTLOOK;

/** Switches the active scheme (future user setting). */
export function setKeymap(keymap: Keymap): void {
  activeKeymap = keymap;
}

export function getShortcuts(action: ShortcutAction): readonly string[] {
  return activeKeymap[action];
}

/** True when `event` matches the chord string. */
export function matchesChord(event: KeyboardEvent, chord: string): boolean {
  const parts = chord.split('+');
  const key = parts.pop() ?? '';
  const mods = new Set(parts.map((p) => p.toLowerCase()));
  const modPressed = event.ctrlKey || event.metaKey;
  // With Alt (Option on macOS) `key` is a special character; letters then match by physical key.
  const keyMatches = event.key.toLowerCase() === key.toLowerCase() || (key.length === 1 && /[a-z]/i.test(key) && event.code === `Key${key.toUpperCase()}`);
  return (
    keyMatches &&
    mods.has('mod') === modPressed &&
    mods.has('shift') === event.shiftKey &&
    mods.has('alt') === event.altKey
  );
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** Human-readable first chord of an action, e.g. `Ctrl+Alt+F` (`⌘⌥F` on macOS), for menus and tooltips. */
export function shortcutLabel(action: ShortcutAction): string {
  const chord = getShortcuts(action)[0] ?? '';
  const parts = chord.split('+');
  if (IS_MAC) return parts.map((p) => ({ Mod: '⌘', Shift: '⇧', Alt: '⌥' })[p] ?? p.toUpperCase()).join('');
  return parts.map((p) => (p === 'Mod' ? 'Ctrl' : p.length === 1 ? p.toUpperCase() : p)).join('+');
}

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export interface ShortcutOptions {
  enabled?: boolean;
  /**
   * Fire while focus is in a text field. Defaults to true only for chords with a modifier
   * (plain keys like Delete or arrows must keep working as text editing keys).
   */
  allowInInputs?: boolean;
}

/** Binds a handler to a keymap action for as long as the component is mounted. */
export function useShortcut(
  action: ShortcutAction,
  handler: (event: KeyboardEvent) => void,
  { enabled = true, allowInInputs }: ShortcutOptions = {},
): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const chord = getShortcuts(action).find((c) => matchesChord(event, c));
      if (!chord) return;
      const hasModifier = chord.includes('+');
      if (!(allowInInputs ?? hasModifier) && isTypingTarget(event.target)) return;
      event.preventDefault();
      handlerRef.current(event);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [action, enabled, allowInInputs]);
}
